import React, { useMemo, useState } from 'react';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { LayoutEditComponentListItem, THUMBNAIL_DIMENSIONS } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutEditComponentListItem';
import { WebsiteEditorMediaGalleryComponentFragment } from '@graphql/generated';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { PhotoGalleryProps } from '../../PhotoGallery.types';
import { PHOTO_GALLERY_ACTIONS } from '../../PhotoGallery.constants';
import { addRendition } from '@shared/utils/photoRendition';
import { PhotoGallery } from '@withjoy/joykit/icons';
import { generateTransparentPlaceholder } from '@shared/utils/generatePlaceholderImageUrl';
import { TextV2 } from '@withjoy/joykit';

export const PhotoGalleryEditListItem: React.FC<PhotoGalleryProps> = props => {
  const { componentData, isLastItem, sortable } = props;

  const [isHover, setIsHover] = useState(false);

  const { t } = useTranslation('websiteEditor');
  const photoGalleryTrans = t('customPage', 'photoGallery');

  const { navigateToScreen } = useNavigationPaneContext();
  const { setCustomComponentBlockData, setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<WebsiteEditorMediaGalleryComponentFragment>();

  const handleEdit = useEventCallback(() => {
    setCustomComponentBlockData(componentData);
    navigateToScreen(PHOTO_GALLERY_ACTIONS.PHOTO_GALLERY_EDIT);
  });

  const handleDeleteClick = useEventCallback(() => {
    if (componentData?.id) {
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: componentData.id,
        title: photoGalleryTrans.deleteTitle(),
        subTitle: photoGalleryTrans.deleteSubtitle(),
        deleteCta: photoGalleryTrans.deleteCta(),
        cancelCta: photoGalleryTrans.cancelCta(),
        successMessage: photoGalleryTrans.deleteComponentSuccess(),
        errorMessage: photoGalleryTrans.deleteComponentError(),
      });
    }
  });

  const mediaURLs = useMemo(() => {
    if (!componentData.mediaCollection?.thumbnails?.length) {
      const placeholder = generateTransparentPlaceholder(THUMBNAIL_DIMENSIONS.width, THUMBNAIL_DIMENSIONS.height, '#ebebeb');
      return [placeholder];
    }
    return componentData.mediaCollection?.thumbnails
      ?.map(media => {
        if (media.__typename === 'MediaPhoto') {
          return addRendition({ url: media.photo.url, renditionSize: 'medium' });
        } else {
          return '';
        }
      })
      .filter(url => !!url);
  }, [componentData.mediaCollection?.thumbnails]);

  return (
    <LayoutEditComponentListItem
      title={<TextV2 typographyVariant="button2">{photoGalleryTrans.photoGalleryCardHeader()}</TextV2>}
      isDragActive={sortable?.isDragActive ?? false}
      dragItemId={sortable?.dragItemId ?? ''}
      isLastItem={isLastItem}
      height={sortable?.itemSize}
      itemId={componentData.id}
      icon={<PhotoGallery size={32} />}
      isHover={isHover}
      setIsHover={setIsHover}
      handleEdit={handleEdit}
      handleDelete={handleDeleteClick}
      thumbnailUrls={mediaURLs}
      cardPaddingY={5}
    />
  );
};
